import { useViewMode } from '@/shared/hooks/useViewMode';
import { Avatar, Chip } from '@/shared/ui/atoms';
import { ViewModeToggle, type ViewModeOption } from '@/shared/ui/ViewModeToggle';
import { SortPicker } from '@/features/project-board/components/SortPicker';
import { DateSort, type SortKey } from '@/features/project-board/sorting';
import { useBoard } from '@/features/project-board/BoardProvider';
import { Workflow, WORKFLOW_LANES } from '@/features/project-board/workflow';
import type { DrawerPayload } from '@/features/project-board/components/DetailDrawer';
import { DrawerPayloads } from '@/features/project-board/drawer-payloads';
import { BUCKETS, type Epic, type Task } from '@/features/project-board/types';
import { ViewFrame } from '@/features/project-board/views/ViewFrame';
import { ItemKey, EpicMembership } from '@/features/project-board/identity';
import { OwnerRoster } from '@/features/project-board/owners';

const DOT: Record<string, string> = {
  todo: 'bg-todo',
  progress: 'bg-progress',
  done: 'bg-done',
  dropped: 'bg-dropped',
  unknown: 'bg-unknown',
};

/** 워크플로 레인 색 — 진행 방향(대기→구현→검증→완료)을 색 온도로 읽히게. */
const LANE_DOT: Record<string, string> = {
  pending: 'bg-todo',
  blocked: 'bg-unknown',
  implemented: 'bg-progress',
  review: 'bg-accent',
  rework: 'bg-unknown',
  done: 'bg-done',
  dropped: 'bg-dropped',
};

const EDGE: Record<string, string> = {
  todo: 'border-l-todo',
  progress: 'border-l-progress',
  done: 'border-l-done',
  dropped: 'border-l-dropped',
  unknown: 'border-l-unknown',
};

type BoardMode = 'bucket' | 'workflow';

const MODES: ReadonlyArray<ViewModeOption<BoardMode>> = [
  { key: 'bucket', label: '상태 4단', glyph: '▦' },
  { key: 'workflow', label: '워크플로', glyph: '⇉' },
];

/** task에는 등록 시점이 없다 — `updatedAt`뿐이라 "갱신" 기준으로만 낼 수 있다(`sorting.ts` 표 참고). */
const SORT_OPTIONS = DateSort.options('갱신');

export function KanbanView(): React.JSX.Element {
  const { index, tasks: scoped, filters } = useBoard();
  const [mode, setMode] = useViewMode<BoardMode>('project-board.kanban.mode', 'bucket');
  const [sort, setSort] = useViewMode<SortKey>('project-board.kanban.sort', 'default');
  const epics = index?.epics ?? [];

  const tasks = DateSort.sort(scoped, sort, (task) => task.updatedAt, (task) => task.uid);

  const lanes = filters.swimlane
    ? epics
        .map((epic) => ({ epic, items: EpicMembership.tasksOf(tasks, epic) }))
        .filter((lane) => lane.items.length > 0)
    : null;

  return (
    <ViewFrame
      scope="tasks"
      countLabel={`${tasks.length}개 task`}
      isEmpty={tasks.length === 0}
      emptyMessage="조건에 맞는 task가 없다."
      toolbar={
        <div className="flex items-center gap-2">
          <SortPicker options={SORT_OPTIONS} value={sort} onChange={setSort} label="task 정렬" />
          <ViewModeToggle options={MODES} value={mode} onChange={setMode} />
        </div>
      }
      render={(open) =>
        lanes ? (
          <div className="flex flex-col gap-5">
            {lanes.map((lane) => (
              <section key={ItemKey.epic(lane.epic)}>
                <header
                  onClick={() => open(DrawerPayloads.epic(lane.epic, index?.tasks ?? []))}
                  className="flex cursor-pointer items-center gap-2 rounded-t-lg border border-line bg-surface px-2.5 py-2 font-semibold"
                >
                  <span>◈ {lane.epic.title}</span>
                  <Chip>
                    {lane.epic.year}-{lane.epic.month}
                  </Chip>
                  <Chip>{lane.items.length}개</Chip>
                </header>
                <Columns
                  tasks={lane.items}
                  epics={epics}
                  allTasks={index?.tasks ?? []}
                  open={open}
                  hideDropped={filters.hideDropped}
                  mode={mode}
                />
              </section>
            ))}
          </div>
        ) : (
          <Columns
            tasks={tasks}
            epics={epics}
            allTasks={index?.tasks ?? []}
            open={open}
            hideDropped={filters.hideDropped}
            mode={mode}
          />
        )
      }
    />
  );
}

function Columns({
  tasks,
  epics,
  allTasks,
  open,
  hideDropped,
  mode,
}: {
  tasks: Task[];
  epics: Epic[];
  /** 에픽 왕복(task → 에픽 → 연결 task)에 필요한 전체 task. 필터와 무관하게 넘긴다. */
  allTasks: Task[];
  open: (payload: DrawerPayload) => void;
  hideDropped: boolean;
  mode: BoardMode;
}): React.JSX.Element {
  // 버킷 모드는 4단(+미분류), 워크플로 모드는 상태어 기준 레인. 어느 쪽도 데이터를 바꾸지 않는다.
  const columns =
    mode === 'workflow'
      ? WORKFLOW_LANES.filter((lane) => !(hideDropped && lane.key === 'dropped')).map((lane) => ({
          key: lane.key,
          label: lane.label,
          match: (task: Task) => Workflow.laneOf(task) === lane.key,
        }))
      : BUCKETS.filter((bucket) => !(hideDropped && bucket.key === 'dropped')).map((bucket) => ({
          key: bucket.key,
          label: bucket.label,
          match: (task: Task) => task.status === bucket.key,
        }));

  return (
    <div className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] items-start gap-3 overflow-x-auto pb-2">
      {columns.map((bucket) => {
        const items = tasks.filter(bucket.match);
        // 빈 레인은 접는다 — 워크플로 모드에서 안 쓰는 상태어까지 칸을 차지하면 가로만 길어진다.
        if (items.length === 0 && (bucket.key === 'unknown' || mode === 'workflow')) return null;
        return (
          <div key={bucket.key} className="min-w-[260px] rounded-lg border border-line-soft bg-[#f0f3f6]">
            <div className="flex items-center gap-1.5 border-b border-line-soft px-3 py-2.5 text-xs font-semibold text-muted">
              <span className={`size-[7px] rounded-full ${DOT[bucket.key] ?? LANE_DOT[bucket.key] ?? 'bg-todo'}`} />
              {bucket.label}
              <span className="ml-auto rounded-full bg-surface px-1.5 text-[11px]">{items.length}</span>
            </div>
            <div className="flex max-h-[calc(100vh-230px)] flex-col gap-2 overflow-y-auto p-2.5">
              {items.map((task) => (
                <article
                  key={task.uid}
                  title={task.title}
                  onClick={() => open(DrawerPayloads.task(task, EpicMembership.epicOf(epics, task), allTasks))}
                  className={`cursor-pointer rounded-md border border-line border-l-[3px] bg-surface p-2.5 hover:border-accent ${EDGE[task.status]}`}
                >
                  {task.titleTag && (
                    <div className="mb-1 inline-block rounded bg-line-soft px-1.5 text-[11px] font-semibold text-muted">
                      {task.titleTag}
                    </div>
                  )}
                  <div className="line-clamp-2 text-[13px] leading-snug">{task.titleHead || '(제목 없음)'}</div>
                  {task.titleDetail && (
                    <div className="mt-1 line-clamp-2 text-[11px] leading-snug text-muted">{task.titleDetail}</div>
                  )}
                  <footer className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="font-mono text-[11px] text-muted">
                      {task.epicSlug}-{task.taskId}
                    </span>
                    {task.repo && <Chip tone="repo">{task.repo}</Chip>}
                    {task.estimate && <Chip>{task.estimate}</Chip>}
                    {task.blockedBy.length > 0 && <Chip tone="warn">⛔ {task.blockedBy.join(',')} 대기</Chip>}
                    <span className="flex-1" />
                    {OwnerRoster.assigneeOf(task, EpicMembership.epicOf(epics, task)) && (
                      <Avatar name={OwnerRoster.assigneeOf(task, EpicMembership.epicOf(epics, task))} />
                    )}
                  </footer>
                </article>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
